/*
 * Vue2 IndexedDB 封装 - ThinkPHP5 风格 Db 链式操作
 * 支持：whereMatch 关键词双向模糊匹配（衣服 裙子 鞋子 => 匹配文本包含任意词）
 */
class IndexedDB {
    constructor() {
        this.dbName = "全球播";
        this.version = 1;
        this.db = null;
        this.stores = [
            { name: "音色", primaryKey: "id", autoIncrement: true, indexes: [] },
            { name: "秘钥", primaryKey: "id", autoIncrement: true, indexes: [] },
            { name: "音色配置", primaryKey: "id", autoIncrement: true, indexes: [] },
            { name: "音色控制配置", primaryKey: "id", autoIncrement: true, indexes: [] },
            { name: "话术管理", primaryKey: "id", autoIncrement: true, indexes: [] },
            { name: "话术列表", primaryKey: "id", autoIncrement: true, indexes: [] },
        ];
    }

    // 打开数据库
    open() {
        return new Promise((resolve, reject) => {
            const request = window.indexedDB.open(this.dbName, this.version);

            request.onupgradeneeded = (e) => {
                this.db = e.target.result;
                this.stores.forEach((table) => {
                    if (!this.db.objectStoreNames.contains(table.name)) {
                        let store = this.db.createObjectStore(table.name, {
                            keyPath: table.primaryKey,
                            autoIncrement: table.autoIncrement,
                        });
                        (table.indexes || []).forEach((idx) => {
                            store.createIndex(idx, idx, { unique: false });
                        });
                    }
                });
            };

            request.onsuccess = () => {
                this.db = request.result;
                resolve(true);
            };

            request.onerror = (e) => reject(e.target.error);
        });
    }

    // 每次获取全新事务
    getStore(storeName, mode = "readonly") {
        if (!this.db) throw new Error("请先调用 init() 初始化数据库");
        const tx = this.db.transaction(storeName, mode);
        return tx.objectStore(storeName);
    }

    // 获取表主键
    getTableKey(tableName) {
        const table = this.stores.find(item => item.name === tableName);
        return table?.primaryKey || 'id';
    }
}

const idb = new IndexedDB();

// ======== ThinkPHP5 风格 Db 链式核心 ========
function Db(table) {
    const query = {
        table: table,
        where: [],
        field: null,
        order: null,
    };

    return {
        where(key, operator, value) {
            if (arguments.length === 2) {
                query.where.push({ key, op: "=", val: operator });
            } else {
                query.where.push({ key, op: operator, val: value });
            }
            return this;
        },

        // ========== 【新增】关键词模糊匹配 ==========
        whereMatch(field, content) {
            query.where.push({
                type: "match",
                key: field,
                text: content,
            });
            return this;
        },

        field(fields) {
            query.field = fields;
            return this;
        },

        order(field, sortType = "ASC") {
            if (arguments.length === 1) {
                const arr = field.split(" ");
                query.order = arr.length === 2
                    ? { field: arr[0], sort: arr[1].toUpperCase() }
                    : { field, sort: "ASC" };
            } else {
                query.order = { field, sort: sortType.toUpperCase() };
            }
            return this;
        },

        async find() {
            const list = await this.select();
            return list[0] || null;
        },

        async select() {
            const store = idb.getStore(query.table);
            return new Promise((resolve, reject) => {
                const req = store.getAll();
                req.onsuccess = () => {
                    let list = req.result || [];
                    list = this._filter(list, query.where);
                    if (query.order) list = this._order(list, query.order);
                    if (query.field) list = list.map(i => this._field(i, query.field));
                    resolve(list);
                };
                req.onerror = () => reject(req.error);
            });
        },

        async add(data) {
            const store = idb.getStore(query.table, "readwrite");
            return new Promise((resolve, reject) => {
                const req = store.add(data);
                req.onsuccess = () => resolve(req.result);
                req.onerror = () => reject(req.error);
            });
        },

        async update(data) {
            const list = await this.select();
            if (!list.length) return 0;
            const store = idb.getStore(query.table, "readwrite");
            await Promise.all(list.map(item => store.put({ ...item, ...data })));
            return list.length;
        },

        async delete() {
            const list = await this.select();
            if (!list.length) return 0;
            const store = idb.getStore(query.table, "readwrite");
            const key = idb.getTableKey(query.table);
            await Promise.all(list.map(item => store.delete(item[key])));
            return list.length;
        },

        // ========== 【集成】关键词过滤逻辑 ==========
        _filter(list, where) {
            if (!where.length) return list;
            return list.filter(item => {
                return where.every(w => {
                    // 关键词匹配逻辑
                    if (w.type === "match") {
                        const keywords = (item[w.key] || "").split(/\s+/).filter(Boolean);
                        const target = w.text || "";
                        // 任意一个关键词包含就匹配成功
                        return keywords.some(word => target.includes(word));
                    }

                    // 原有普通条件
                    const val = item[w.key];
                    switch (w.op) {
                        case "=": return val == w.val;
                        case ">": return val > w.val;
                        case "<": return val < w.val;
                        case ">=": return val >= w.val;
                        case "<=": return val <= w.val;
                        case "!=": return val != w.val;
                        default: return true;
                    }
                });
            });
        },

        _order(list, order) {
            return [...list].sort((a, b) => {
                let valA = a[order.field];
                let valB = b[order.field];
                if (!isNaN(valA) && !isNaN(valB)) {
                    valA = Number(valA);
                    valB = Number(valB);
                }
                if (valA > valB) return order.sort === "ASC" ? 1 : -1;
                if (valA < valB) return order.sort === "ASC" ? -1 : 1;
                return 0;
            });
        },

        _field(item, fields) {
            const res = {};
            fields.split(",").forEach(k => {
                const key = k.trim();
                res[key] = item[key];
            });
            return res;
        },
    };
}

export default {
    install(Vue) {
        Vue.prototype.$idb = idb;
        Vue.prototype.Db = Db;
    },
    init: () => idb.open(),
};